import { HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { asyncScheduler, combineLatest, Observable, throwError, timer } from 'rxjs';
import { catchError, filter, map, switchMap, tap, throttleTime } from 'rxjs/operators';

import { allUploadItemsSelector, selectAll } from '@state/selectors/uploadItem.selector';

import {
    ApiRecordType,
    CustomErrorType,
    FacialRecognitionConsent,
    FragmentId,
    GenerateGuid,
    IUploadItem,
    MediaObjectId,
    UploadItem,
    UploadStatus,
    UploadType
} from '@stylehaven/components';
import { REPRESENTATION_GROUP } from '../../constants/upload-item-group.constants';
import { SharedUploadSandboxService } from './shared-upload.sandbox.service';

@Injectable({ providedIn: 'root' })
export class DirectUploadSandboxService extends SharedUploadSandboxService {
    private readonly AUTO_ABORT_TIME_IN_SECONDS = 60;
    // Ensure check after upload finished
    protected readonly AFTER_DIRECT_UPLOAD_FINISHED_STATUS: UploadStatus = UploadStatus.FINISHED;

    constructor() {
        super();
    }

    public init() {
        this.initProgressListener();
        this.initStalledUploadsListener();
    }

    // #region Upload
    public uploadFile(
        file: File,
        config: {
            zoneId: string;
            parentRecordId: MediaObjectId;
            recordType: ApiRecordType;
            group?: string;
            facialRecognitionConsent?: FacialRecognitionConsent;
        }
    ): void {
        this.uploadSingleFile(file, {
            ...config,
            group: config.group ?? GenerateGuid()
        }).subscribe();
    }

    private uploadSingleFile(
        file: File,
        config: {
            zoneId: string;
            parentRecordId: MediaObjectId;
            recordType: ApiRecordType;
            group: string;
            facialRecognitionConsent?: FacialRecognitionConsent;
        }
    ): Observable<HttpEvent<unknown>> {
        const uploadItem: IUploadItem = this.initNewUploadItem(file, config.group);
        const cancel$: Observable<string> = this.initCancelUploadItemStream(uploadItem);

        return this.sendFileToBackend(file, uploadItem, cancel$, config);
    }

    public uploadFiles(
        files: FileList,
        config: {
            zoneId: string;
            parentRecordId: MediaObjectId;
            recordType: ApiRecordType;
            group?: string;
            facialRecognitionConsent?: FacialRecognitionConsent;
        }
    ): Observable<boolean> {
        const group = config.group ?? GenerateGuid();
        return combineLatest(
            Array.from(files).map(file =>
                this.uploadToApiFragment(
                    this.uploadSingleFile(file, {
                        ...config,
                        group
                    })
                )
            )
        ).pipe(
            switchMap(fragments => this.checkIndexed(fragments)),
            tap(() => this.finishGroupToast(group)),
            map(() => true)
        );
    }

    public uploadNewOriginal(file: File, fragmentId: FragmentId): Observable<boolean> {
        const uploadItem: IUploadItem = this.initNewUploadItem(file, REPRESENTATION_GROUP, fragmentId);
        const cancel$: Observable<string> = this.initCancelUploadItemStream(uploadItem);

        return this.uploadToApiFragment(
            this.sendRepresentationFileToBackend(file, uploadItem, cancel$, fragmentId)
        ).pipe(
            switchMap(record => this.checkIndexed([record])),
            catchError(error => {
                if (error === CustomErrorType.IndexTimeout) {
                    this.handleUploadTimeout(uploadItem);
                }
                return throwError(() => error);
            }),
            map(() => true)
        );
    }

    private sendFileToBackend(
        file: File,
        uploadItem: IUploadItem,
        cancel$: Observable<string>,
        config: {
            zoneId: string;
            recordType: ApiRecordType;
            parentRecordId?: MediaObjectId;
            facialRecognitionConsent?: FacialRecognitionConsent;
        }
    ): Observable<HttpEvent<unknown>> {
        this.initUploadItem(uploadItem);
        this.startGroupToast(uploadItem.group);

        return this.directRecordUpload(file, uploadItem, cancel$, config);
    }

    private sendRepresentationFileToBackend(
        file: File,
        uploadItem: IUploadItem,
        cancel$: Observable<string>,
        fragmentId: FragmentId
    ): Observable<HttpEvent<unknown>> {
        this.initUploadItem(uploadItem);
        this.startGroupToast(uploadItem.group);

        return this.directRepresentationUpload(file, uploadItem, cancel$, fragmentId);
    }

    // #endregion

    // #region Cancel upload

    public cancelUpload(syncData: { tempId: string; uploadItemUpdate: Partial<IUploadItem> }): void {
        this.cancelUploadItem$.next(syncData.tempId);

        this.syncUploadItem(syncData.tempId, syncData.uploadItemUpdate);
    }
    // #endregion

    // #region Pause upload
    public isPausable(upload: IUploadItem): boolean {
        return false;
    }

    public pauseUpload(uploadItem: IUploadItem): void {
        throw new Error('Pausing upload is not implemented.');
    }
    // #endregion

    // #region Resume upload
    public isResumable(upload: IUploadItem): boolean {
        return false;
    }

    public resumeUpload(uploadItem: IUploadItem): void {
        throw new Error('Resuming upload is not implemented.');
    }
    // #endregion

    // #region BrowserRefresh
    public onBrowserRefresh(): Observable<void> {
        // Cancel all running uploads
        return this.store
            .select(selectAll)
            .pipe(
                map(uploads =>
                    uploads
                        .filter(upload => this.isRunningUpload(upload))
                        .forEach(upload => this.cancelOrAbortUpload(upload, true))
                )
            );
    }
    // #endregion

    // #region Stalled Uploads
    private initStalledUploadsListener() {
        const uploads$ = this.store
            .select(allUploadItemsSelector)
            .pipe(throttleTime(300, asyncScheduler, { leading: false, trailing: true }));

        const stalledUploads$: Observable<UploadItem[]> = timer(0, 1000 * 5).pipe(
            switchMap(() => uploads$),
            map(uploads => uploads.filter(upload => this.isStalledUpload(upload))),
            filter(stalledUploads => stalledUploads.length > 0)
        );

        // Automatically abort stalled uploads
        stalledUploads$.subscribe(stalledUploads =>
            stalledUploads.forEach(stalledUpload => this.cancelOrAbortUpload(stalledUpload, true))
        );
    }

    /**
     * Upload is considered stalled when the progress has not updated after defined AUTO_ABORT_TIME_IN_SECONDS
     * @param upload
     * @returns boolean
     */
    private isStalledUpload(upload: UploadItem): boolean {
        return (
            upload.state.status === UploadStatus.UPLOADING &&
            this.getDateDifferenceInSeconds(new Date(), upload.lastProgressUpdate) >= this.AUTO_ABORT_TIME_IN_SECONDS
        );
    }

    private getDateDifferenceInSeconds(firstDate: Date, secondDate: Date): number {
        return (firstDate.getTime() - secondDate.getTime()) / 1000;
    }
    // #endregion

    // #region UploadItem
    private initNewUploadItem(file: File, group: string, parent?: FragmentId): IUploadItem {
        const now: Date = new Date();

        return {
            tempId: GenerateGuid(),
            name: file.name,
            type: UploadType.DIRECT,
            ...(parent ? { parent: { fragmentId: parent } } : {}),
            state: {
                status: UploadStatus.WAITING,
                progress: 0
            },
            date: now,
            group,
            lastProgressUpdate: now
        };
    }

    // #endregion
}
